/**
 * IDDL Phase 3b — Concept Config admin (edit-enabled).
 *
 * Lists every concept_config in the database and lets the admin author
 * the behavioural contract for any of them. The previous Phase-0 view
 * was read-only; Phase 3b unlocks the editor so the team can prepare
 * the remaining 33 concepts' contracts ahead of their primitives.
 *
 * What can be edited (subset enforced server-side too):
 *   • simulation_type            (dropdown · canonical primitives)
 *   • scenario_text              (textarea · JSON if non-string)
 *   • candidate_goal             (textarea)
 *   • available_actions          (comma-separated)
 *   • required_events            (comma-separated)
 *   • critical_merit_events      (comma-separated)
 *   • auto_fail_events           (comma-separated)
 *   • valid_final_decisions      (comma-separated)
 *   • sequence_rules             (JSON · array of {before, after})
 *   • reflection_prompt          (textarea)
 *   • reflection_required_themes (comma-separated)
 *   • remediation_message        (textarea)
 *
 * NEVER editable: concept_id, concept_name, domain, subdomain (those
 * are the published Body-of-Knowledge taxonomy and are locked).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { api, adminConceptConfigsXlsxUrl, adminToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useT } from "@/lib/i18n";
import {
  Loader2, ChevronRight, Cpu, FlaskConical, Pencil, X, Save,
  Sparkles, Download, Upload, AlertTriangle, FileSpreadsheet,
} from "lucide-react";
import { toast } from "sonner";
import DownloadButton from "@/components/DownloadButton";
import LanguagePackEditor from "./LanguagePackEditor";

const SIM_BADGE_COLOR = {
  iddl_browser:           "bg-sky-100 text-sky-900 border-sky-300",
  iddl_inbox:             "bg-rose-100 text-rose-900 border-rose-300",
  iddl_ai_chat:           "bg-violet-100 text-violet-900 border-violet-300",
  iddl_file_manager:      "bg-amber-100 text-amber-900 border-amber-300",
  iddl_settings:          "bg-slate-100 text-slate-900 border-slate-300",
  iddl_workflow:          "bg-emerald-100 text-emerald-900 border-emerald-300",
  iddl_editor:            "bg-teal-100 text-teal-900 border-teal-300",
  // Phase 1 (six-primitive) — registered IDs; renderers not shipped yet.
  iddl_ai_evaluate:       "bg-fuchsia-100 text-fuchsia-900 border-fuchsia-300",
  iddl_ai_decision:       "bg-indigo-100 text-indigo-900 border-indigo-300",
  iddl_ai_chat_agent:     "bg-violet-50 text-violet-900 border-violet-400 border-dashed",
  iddl_ai_evaluate_agent: "bg-fuchsia-50 text-fuchsia-900 border-fuchsia-400 border-dashed",
  iddl_ai_decision_agent: "bg-indigo-50 text-indigo-900 border-indigo-400 border-dashed",
};

const ENV_BADGE_COLOR = {
  ai:         "bg-white text-zinc-800 border-zinc-400",
  agentic_ai: "bg-amber-50 text-amber-900 border-amber-400",
};

const csvToList = (s) => (s || "").split(",").map((x) => x.trim()).filter(Boolean);
const listToCsv = (l) => (Array.isArray(l) ? l.join(", ") : "");
const stringify = (v) => {
  if (v == null) return "";
  if (typeof v === "string") return v;
  try { return JSON.stringify(v, null, 2); } catch (_e) { return String(v); }
};

export default function ConceptConfig() {
  const { t } = useT();
  const [rows, setRows] = useState([]);
  const [simTypes, setSimTypes] = useState([]);
  const [open, setOpen] = useState({});
  const [busy, setBusy] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  // Phase 3-followup state
  const [drafting, setDrafting] = useState(false);
  const [draftResult, setDraftResult] = useState(null);
  const [importing, setImporting] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const [importFile, setImportFile] = useState(null);
  const fileInputRef = useRef(null);
  // Iter-26 (P1) — which row currently has its language-pack editor
  // expanded. Only one at a time to keep the page navigable.
  const [showLangPackFor, setShowLangPackFor] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/concept_configs").then((x) => x.data);
      setRows(r.configs || []);
      setSimTypes(r.canonical_sim_types || []);
      // Phase 6 · clear stale env-tab selections whose keys may no
      // longer exist after a catalogue reload; the groups useMemo
      // then falls back to docs[0] (env-order sorted → "ai" first).
      setActiveEnv({});
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load concept configs");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  // Phase 6 · Aggregate stored (concept_id, environment) documents
  // into ONE conceptual row per concept_id. The Admin surface must
  // show 36 concepts (12 DS + 12 CS + 12 AI), not 39 configs.
  // For AI concepts with both operationalisations, the active env
  // is toggled via a per-concept tab (state: `activeEnv[cid]`).
  // DS / CS concepts have no env variants and no toggle is rendered.
  const [activeEnv, setActiveEnv] = useState({});  // { [cid]: env }
  const groups = useMemo(() => {
    const byCid = new Map();
    for (const doc of rows) {
      const list = byCid.get(doc.concept_id) || [];
      list.push(doc);
      byCid.set(doc.concept_id, list);
    }
    // Sort envs so "ai" / null (base) comes first, then "agentic_ai".
    const envOrder = (e) => (e === null ? 0 : e === "ai" ? 1 : 2);
    const out = [];
    for (const [cid, docs] of byCid.entries()) {
      docs.sort((a, b) => envOrder(a.environment) - envOrder(b.environment));
      const activeEnvKey = activeEnv[cid];
      // Phase 7 · When the admin hasn't yet clicked a tab, default
      // the active row env to the concept's persisted
      // `selected_environment` (stored on the base row).
      const baseDoc = docs.find((d) => d.environment !== "agentic_ai") || docs[0];
      const persistedEnv = baseDoc?.selected_environment ?? baseDoc?.environment ?? null;
      const resolvedKey = activeEnvKey ?? (persistedEnv ?? "__null__");
      const active =
        docs.find((d) => (d.environment ?? "__null__") === resolvedKey)
        || docs[0];
      out.push({
        concept_id: cid,
        concept_name: active.concept_name,
        domain: active.domain,
        domain_name: active.domain_name,
        subdomain: active.subdomain,
        envs: docs,           // full env list for the env tabs
        active,               // currently-displayed contract doc
        base: baseDoc,        // Phase 7 · base row (source of truth for
                              // active_in_sequence + selected_environment)
      });
    }
    // Preserve the API order by first-occurrence
    const originalOrder = [];
    const seen = new Set();
    for (const d of rows) {
      if (seen.has(d.concept_id)) continue;
      seen.add(d.concept_id);
      originalOrder.push(d.concept_id);
    }
    out.sort((a, b) => originalOrder.indexOf(a.concept_id) - originalOrder.indexOf(b.concept_id));
    return out;
  }, [rows, activeEnv]);

  const toggleEngine = async (cid, currentVal, env) => {
    setBusy((b) => ({ ...b, [cid]: true }));
    try {
      const r = await api
        .post(`/admin/concept_configs/${cid}/toggle_engine`, {
          value: !currentVal,
          environment: env ?? null,
        })
        .then((x) => x.data);
      setRows((rs) =>
        rs.map((row) =>
          (row.concept_id === cid && (row.environment ?? null) === (env ?? null))
            ? { ...row, use_new_engine: r.use_new_engine }
            : row,
        ),
      );
      toast.success(
        r.use_new_engine
          ? `${cid} → NEW engine`
          : `${cid} → legacy engine`,
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Toggle failed");
    } finally {
      setBusy((b) => ({ ...b, [cid]: false }));
    }
  };

  const startEdit = (row) => {
    setEditingId(row.concept_id);
    const themes = row.reflection_required_themes || [];
    // If any theme is an object (not a string), preserve fidelity via JSON.
    const themesArePlain = Array.isArray(themes) && themes.every((t) => typeof t === "string");
    setDraft({
      simulation_type: row.simulation_type || "",
      scenario_text_raw: stringify(row.scenario_text),
      candidate_goal: row.candidate_goal || "",
      candidate_task: row.candidate_task || "",
      available_actions_csv: listToCsv(row.available_actions),
      required_events_csv: listToCsv(row.required_events),
      critical_merit_events_csv: listToCsv(row.critical_merit_events),
      auto_fail_events_csv: listToCsv(row.auto_fail_events),
      valid_final_decisions_csv: listToCsv(row.valid_final_decisions),
      sequence_rules_raw: stringify(row.sequence_rules || []),
      reflection_prompt: row.reflection_prompt || "",
      reflection_themes_mode: themesArePlain ? "csv" : "json",
      reflection_themes_csv: themesArePlain ? listToCsv(themes) : "",
      reflection_themes_raw: themesArePlain ? "" : stringify(themes),
      remediation_message: row.remediation_message || "",
    });
  };

  const cancelEdit = () => { setEditingId(null); setDraft(null); };

  const saveEdit = async (cid, env) => {
    if (!draft) return;
    // Parse JSON fields with helpful errors
    let scenarioText;
    const rawSc = (draft.scenario_text_raw || "").trim();
    if (rawSc.startsWith("{") || rawSc.startsWith("[")) {
      try { scenarioText = JSON.parse(rawSc); }
      catch (e) { toast.error(`scenario_text: invalid JSON — ${e.message}`); return; }
    } else {
      scenarioText = rawSc;
    }
    let sequenceRules;
    const rawSeq = (draft.sequence_rules_raw || "").trim();
    if (!rawSeq) {
      sequenceRules = [];
    } else {
      try {
        sequenceRules = JSON.parse(rawSeq);
        if (!Array.isArray(sequenceRules)) throw new Error("sequence_rules must be an array");
        for (const it of sequenceRules) {
          if (!it || typeof it !== "object" || !it.before || !it.after) {
            throw new Error('each rule must be {"before": "...", "after": "..."}');
          }
        }
      } catch (e) { toast.error(`sequence_rules: ${e.message}`); return; }
    }
    let reflectionThemes;
    if (draft.reflection_themes_mode === "json") {
      const rawTh = (draft.reflection_themes_raw || "").trim();
      if (!rawTh) {
        reflectionThemes = [];
      } else {
        try {
          reflectionThemes = JSON.parse(rawTh);
          if (!Array.isArray(reflectionThemes)) {
            throw new Error("reflection_required_themes must be an array");
          }
        } catch (e) {
          toast.error(`reflection_required_themes: ${e.message}`);
          return;
        }
      }
    } else {
      reflectionThemes = csvToList(draft.reflection_themes_csv);
    }
    const patch = {
      simulation_type: draft.simulation_type || "",
      scenario_text: scenarioText,
      candidate_goal: draft.candidate_goal,
      candidate_task: draft.candidate_task,
      available_actions: csvToList(draft.available_actions_csv),
      required_events: csvToList(draft.required_events_csv),
      critical_merit_events: csvToList(draft.critical_merit_events_csv),
      auto_fail_events: csvToList(draft.auto_fail_events_csv),
      valid_final_decisions: csvToList(draft.valid_final_decisions_csv),
      sequence_rules: sequenceRules,
      reflection_prompt: draft.reflection_prompt,
      reflection_required_themes: reflectionThemes,
      remediation_message: draft.remediation_message,
    };
    setSaving(true);
    try {
      const url = env
        ? `/admin/concept_configs/${cid}?environment=${encodeURIComponent(env)}`
        : `/admin/concept_configs/${cid}`;
      const updated = await api.put(url, patch).then((x) => x.data);
      setRows((rs) => rs.map((row) =>
        (row.concept_id === cid && (row.environment ?? null) === (env ?? null))
          ? updated
          : row,
      ));
      toast.success(`${cid} contract saved · v${updated.config_version}`);
      cancelEdit();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  // Phase 6 · conceptual counters (36 concepts) vs storage counters
  // (39 operationalisations). The Admin surface reports the
  // conceptual count as the headline; storage count is a secondary
  // metric.
  const conceptCount = groups.length;
  const opCount = rows.length;
  // "Uses new engine" counted at the CONCEPT level — any op with
  // new engine flips the concept's badge to NEW.
  const newEngineCount = groups.filter((g) => g.envs.some((e) => e.use_new_engine)).length;
  const authoredCount = groups.filter((g) =>
    g.envs.some((e) => (e.required_events || []).length > 0),
  ).length;
  const draftCount = groups.filter((g) => g.envs.some((e) => e.draft_generated)).length;

  const runGenerateDrafts = async () => {
    if (drafting) return;
    setDrafting(true);
    try {
      const r = await api
        .post("/admin/concept_configs/generate_drafts", {})
        .then((x) => x.data);
      setDraftResult(r);
      toast.success(
        r.total_generated > 0
          ? `Generated drafts for ${r.total_generated} concepts (skipped ${r.total_skipped})`
          : `Nothing to draft — every concept is already authored or admin-edited (skipped ${r.total_skipped})`,
      );
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Draft generation failed");
    } finally {
      setDrafting(false);
    }
  };

  const onImportPick = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setImportFile(file);
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const r = await api
        .post("/admin/concept_configs/bulk_import?dry_run=true", form, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((x) => x.data);
      setImportPreview(r);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not preview workbook");
      setImportFile(null);
    } finally {
      setImporting(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const commitImport = async () => {
    if (!importFile) return;
    setImporting(true);
    try {
      const form = new FormData();
      form.append("file", importFile);
      const r = await api
        .post("/admin/concept_configs/bulk_import?dry_run=false", form, {
          headers: { "Content-Type": "multipart/form-data" },
        })
        .then((x) => x.data);
      toast.success(
        `Applied ${r.total_applied} concept(s) · ${r.total_no_change} unchanged · ${r.total_failed} failed`,
      );
      setImportPreview(null);
      setImportFile(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk import failed");
    } finally {
      setImporting(false);
    }
  };

  const cancelImport = () => {
    setImportPreview(null);
    setImportFile(null);
  };

  if (loading) {
    return (
      <div className="mt-8 flex items-center gap-2 text-sm text-[var(--iddl-muted)]" data-testid="concept-config-loading">
        <Loader2 className="animate-spin" size={14} /> {t("common.loading")}
      </div>
    );
  }

  return (
    <div className="mt-8" data-testid="concept-config-tab">
      <div className="flex items-center gap-3 text-sm text-[var(--iddl-muted)] mb-4">
        <FlaskConical size={14} />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-x-1 gap-y-1">
            <strong>Assessment Contracts.</strong>
            <span data-testid="concept-count-headline"><strong>{conceptCount}</strong> concepts</span>
            <span className="text-[var(--iddl-muted)]" data-testid="operationalisation-count">
              ({opCount} operationalisations across environments)
            </span>
            <span>· {simTypes.length} canonical primitives:</span>
            {simTypes.map((s) => (
              <code key={s} className="label-mono">{s}</code>
            ))}
          </div>
          <div className="mt-1">
            <strong>{authoredCount}</strong> of <strong>{conceptCount}</strong>{" "}
            concepts have a non-empty required_events contract ·{" "}
            <strong>{newEngineCount}</strong> currently routed through the new engine ·{" "}
            <strong>{draftCount}</strong> currently labelled <em>draft</em>.
          </div>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap items-center gap-2 border border-[var(--iddl-rule)] bg-[var(--iddl-paper)] px-3 py-2" data-testid="concept-config-toolbar">
        <span className="label-mono text-[var(--iddl-muted)] mr-2">Bulk tools:</span>
        <Button
          size="sm"
          onClick={runGenerateDrafts}
          disabled={drafting}
          className="rounded-none btn-ink"
          data-testid="btn-generate-drafts"
        >
          {drafting ? <Loader2 size={12} className="mr-2 animate-spin" /> : <Sparkles size={12} className="mr-2" />}
          Generate drafts from legacy
        </Button>
        <DownloadButton
          url={`${adminConceptConfigsXlsxUrl()}?admin_token=${encodeURIComponent(adminToken.get() || "")}`}
          filename="IDDL_Concept_Configs.xlsx"
          label="Export all (.xlsx)"
          icon={<Download size={12} className="ml-2" />}
          data-testid="btn-config-export-xlsx"
        />
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx"
          onChange={onImportPick}
          className="hidden"
          data-testid="input-config-import-file"
        />
        <Button
          size="sm"
          variant="outline"
          className="rounded-none"
          disabled={importing}
          onClick={() => fileInputRef.current?.click()}
          data-testid="btn-config-import-xlsx"
        >
          {importing && !importPreview ? (
            <Loader2 size={12} className="mr-2 animate-spin" />
          ) : (
            <Upload size={12} className="mr-2" />
          )}
          Bulk import (.xlsx)
        </Button>
        <span className="label-mono text-xs text-[var(--iddl-muted)] ml-2">
          Generator never touches admin-edited concepts (v&gt;1) and never auto-activates the new engine.
        </span>
      </div>

      {draftResult && (
        <div className="mb-4 border border-[var(--iddl-rule)] bg-emerald-50 px-3 py-2 text-xs" data-testid="draft-result">
          <strong>Draft generator result:</strong> generated {draftResult.total_generated},
          skipped {draftResult.total_skipped}.
          {draftResult.generated.length > 0 && (
            <details className="mt-1">
              <summary className="cursor-pointer">Show details</summary>
              <div className="mt-2">
                <div className="label-mono">Generated:</div>
                <div className="font-mono">{draftResult.generated.join(", ")}</div>
                {draftResult.skipped.length > 0 && (
                  <>
                    <div className="label-mono mt-2">Skipped:</div>
                    <ul className="list-disc pl-5">
                      {draftResult.skipped.map((s) => (
                        <li key={s.concept_id}>
                          <code>{s.concept_id}</code> — {s.reason}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </details>
          )}
        </div>
      )}

      {importPreview && (
        <BulkImportPreviewModal
          preview={importPreview}
          file={importFile}
          onCommit={commitImport}
          onCancel={cancelImport}
          committing={importing}
        />
      )}

      <div className="border border-[var(--iddl-rule)] divide-y divide-[var(--iddl-rule)]" data-testid="concept-config-list">
        {groups.map((g) => {
          const r = g.active;
          const base = g.base;                  // Phase 7 · base row (source of truth for active_in_sequence)
          const baseEnv = base?.environment ?? null;
          const activeEnvVal = r.environment;
          const isOpen = !!open[r.concept_id];
          const isEditing = editingId === r.concept_id;
          const isMulti = g.envs.length > 1;
          return (
            <div key={r.concept_id} data-testid={`config-row-${r.concept_id}`}>
              <div
                className="w-full flex flex-wrap items-center gap-3 px-4 py-3 hover:bg-[var(--iddl-paper)]"
                data-testid={`config-toggle-${r.concept_id}`}
              >
                <button
                  type="button"
                  onClick={() => setOpen({ ...open, [r.concept_id]: !isOpen })}
                  className="flex items-start gap-2 text-left flex-1 min-w-0 basis-[22rem] shrink-0"
                >
                  <ChevronRight
                    size={14}
                    className={`mt-1 shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`}
                  />
                  <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0 min-w-0">
                    <code
                      className="label-mono whitespace-nowrap"
                      data-testid={`config-code-${r.concept_id}`}
                    >
                      {r.concept_id}
                    </code>
                    <span
                      className="font-display font-semibold break-words min-w-0"
                      data-testid={`config-name-${r.concept_id}`}
                    >
                      {r.concept_name}
                    </span>
                    <span className="label-mono text-[var(--iddl-muted)] hidden lg:inline whitespace-nowrap">
                      {r.domain_name || r.domain}
                    </span>
                  </div>
                </button>

                {/* Phase 7 · Environment selector directly on the row.
                    Rendered for every AI Competency concept — the
                    dropdown determines the persisted
                    `selected_environment` and which
                    (concept_id, environment) contract is loaded in
                    the expanded panel. */}
                {r.domain === "AI" && (
                  <label className="flex items-center gap-2" data-testid={`config-env-selector-${r.concept_id}`}>
                    <span className="label-mono text-xs text-[var(--iddl-muted)]">Env</span>
                    <select
                      value={activeEnvVal === "agentic_ai" ? "agentic_ai" : "ai"}
                      onChange={async (e) => {
                        const nextEnv = e.target.value;  // "ai" | "agentic_ai"
                        setActiveEnv({ ...activeEnv, [r.concept_id]: nextEnv });
                        try {
                          await api.put(`/admin/concept_configs/${r.concept_id}/selection`,
                            { selected_environment: nextEnv });
                          // Reflect the persisted flag on the BASE row so a
                          // subsequent render keeps the dropdown value stable
                          // across reload without needing a full refetch.
                          setRows((rs) => rs.map((row) =>
                            (row.concept_id === r.concept_id && (row.environment ?? null) === baseEnv)
                              ? { ...row, selected_environment: nextEnv }
                              : row));
                        } catch (err) {
                          toast.error(err?.response?.data?.detail || "Env change failed");
                        }
                      }}
                      className="text-xs px-2 py-1 border border-[var(--iddl-rule)] bg-white"
                      onClick={(e) => e.stopPropagation()}
                      data-testid={`config-env-select-${r.concept_id}`}
                    >
                      <option value="ai" data-testid={`config-env-option-${r.concept_id}-ai`}>AI</option>
                      <option value="agentic_ai" data-testid={`config-env-option-${r.concept_id}-agentic_ai`}>Agentic AI</option>
                    </select>
                  </label>
                )}

                {/* Primitive — auto-resolved, read-only */}
                {r.simulation_type ? (
                  <span
                    className={`label-mono px-2 py-1 border ${SIM_BADGE_COLOR[r.simulation_type] || ""}`}
                    data-testid={`config-sim-${r.concept_id}`}
                    title="Primitive is auto-resolved from subdomain + environment"
                  >
                    {r.simulation_type}
                  </span>
                ) : (
                  <span className="label-mono px-2 py-1 border border-dashed border-[var(--iddl-rule)] text-[var(--iddl-muted)]">
                    no sim
                  </span>
                )}

                {/* New engine state + Revert to legacy */}
                <span
                  className={`label-mono px-2 py-1 border ${r.use_new_engine ? "bg-emerald-100 text-emerald-900 border-emerald-300" : "bg-zinc-100 text-zinc-700 border-zinc-300"}`}
                  data-testid={`config-engine-${r.concept_id}`}
                >
                  {r.use_new_engine ? "NEW ENGINE" : "legacy"}
                </span>
                <span
                  onClick={(e) => { e.stopPropagation(); toggleEngine(r.concept_id, r.use_new_engine, activeEnvVal); }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleEngine(r.concept_id, r.use_new_engine, activeEnvVal);
                    }
                  }}
                  className="inline-flex items-center gap-1 text-xs border border-[var(--iddl-rule)] px-2 py-1 hover:bg-[var(--iddl-surface)] cursor-pointer"
                  data-testid={`btn-toggle-engine-${r.concept_id}`}
                >
                  {busy[r.concept_id] ? (
                    <Loader2 className="animate-spin" size={12} />
                  ) : (
                    <Cpu size={12} />
                  )}
                  {r.use_new_engine ? "Revert to legacy" : "Use new engine"}
                </span>

                {/* Phase 7 · Assessment activation slider — separate
                    from Use New Engine. Persists to concept.active_in_sequence.
                    Sourced from the BASE row (not the currently-displayed
                    env doc) so agentic-selected AI concepts still reflect
                    the correct ON/OFF state. */}
                <label className="inline-flex items-center gap-2 cursor-pointer"
                       title="Include this concept in the candidate assessment sequence">
                  <span className="label-mono text-xs text-[var(--iddl-muted)]">Assessment</span>
                  <input
                    type="checkbox"
                    checked={!!base?.active_in_sequence}
                    onChange={async (e) => {
                      const next = e.target.checked;
                      try {
                        await api.put(`/admin/concept_configs/${r.concept_id}/selection`, { active_in_sequence: next });
                        setRows((rs) => rs.map((row) =>
                          (row.concept_id === r.concept_id && (row.environment ?? null) === baseEnv)
                            ? { ...row, active_in_sequence: next }
                            : row));
                      } catch (err) {
                        toast.error(err?.response?.data?.detail || "Activation failed");
                      }
                    }}
                    className="sr-only peer"
                    data-testid={`config-activation-${r.concept_id}`}
                  />
                  <span className={`relative w-9 h-5 rounded-full transition-colors ${base?.active_in_sequence ? "bg-emerald-500" : "bg-zinc-300"}`}
                        data-testid={`config-activation-switch-${r.concept_id}`}
                        aria-hidden="true">
                    <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white transition-all ${base?.active_in_sequence ? "left-4" : "left-0.5"}`} />
                  </span>
                </label>
              </div>

              {/* Phase 7 · Row-level env tabs are already rendered
                  above in the row header. Expanded panel shows the
                  active env as a read-only badge for reference. */}
              {/* Phase 8 · Prominent expanded-contract header — shown
                  on every open row (both AI multi-env and DS/CS
                  single-env). Concept identity is repeated at the
                  top of the panel so the contract content is
                  immediately anchored to a specific concept, and a
                  metadata sub-line captures Environment · Primitive ·
                  Version · Hash for the currently-selected env. */}
              {isOpen && (
                <div
                  className="px-4 pt-3 pb-1 bg-[var(--iddl-paper)]"
                  data-testid={`config-expanded-header-${r.concept_id}`}
                >
                  <div
                    className="flex flex-wrap items-baseline gap-x-2 gap-y-1"
                    data-testid={`config-expanded-title-${r.concept_id}`}
                  >
                    <code className="label-mono text-base whitespace-nowrap">
                      {r.concept_id}
                    </code>
                    <span className="text-base font-display font-semibold">·</span>
                    <span className="text-base font-display font-semibold break-words">
                      {r.concept_name}
                    </span>
                  </div>
                  <div
                    className="mt-1 text-xs text-[var(--iddl-muted)] flex flex-wrap items-center gap-x-2 gap-y-0"
                    data-testid={`config-expanded-meta-${r.concept_id}`}
                  >
                    <span>
                      Environment:{" "}
                      <strong data-testid={`config-expanded-env-${r.concept_id}`}>
                        {r.environment === "agentic_ai" ? "Agentic AI"
                          : r.environment === "ai" ? "AI"
                          : r.environment || "—"}
                      </strong>
                    </span>
                    <span>·</span>
                    <span>
                      Primitive:{" "}
                      <code
                        className="label-mono"
                        data-testid={`config-expanded-primitive-${r.concept_id}`}
                      >
                        {(r.simulation_type || "").toUpperCase()}
                      </code>
                    </span>
                    <span>·</span>
                    <span>
                      Contract v
                      <code className="label-mono">{r.config_version}</code>
                    </span>
                    <span>·</span>
                    <span>
                      hash <code className="label-mono">{r.config_hash}</code>
                    </span>
                  </div>
                </div>
              )}

              {isOpen && !isEditing && (
                <div className="px-4 pb-4 pt-1 bg-[var(--iddl-paper)]" data-testid={`config-detail-${r.concept_id}`}>
                  {/* ── GOAL / COMPETENCY + TASK BANNER ───────────────
                      The competency goal anchors WHAT this assessment
                      measures. The behavioural task is the literal,
                      action-oriented instruction the candidate reads
                      when the simulation opens. Showing both makes the
                      gap between competency outcome and operational
                      task explicit for fairness review. */}
                  <div className="border-l-4 border-[var(--iddl-ink)] bg-white px-4 py-3 mb-4" data-testid={`config-behavioural-task-${r.concept_id}`}>
                    <div className="label-mono mb-1 text-[var(--iddl-muted)]">Task · what the candidate is asked to do</div>
                    {r.candidate_task ? (
                      <p className="text-sm leading-relaxed" data-testid={`config-task-text-${r.concept_id}`}>
                        {r.candidate_task}
                      </p>
                    ) : (
                      <p className="text-sm italic text-amber-700" data-testid={`config-task-missing-${r.concept_id}`}>
                        ⚠ No behavioural task authored yet. Edit the
                        contract to add the literal instruction the
                        candidate reads when the simulation opens.
                      </p>
                    )}
                    <div className="mt-3 pt-3 border-t border-[var(--iddl-rule)]">
                      <div className="label-mono mb-1 text-[var(--iddl-muted)]">Goal · competency statement</div>
                      {r.candidate_goal ? (
                        <p className="text-sm leading-relaxed font-display" data-testid={`config-goal-text-${r.concept_id}`}>
                          {r.candidate_goal}
                        </p>
                      ) : (
                        <p className="text-sm italic text-rose-700" data-testid={`config-goal-missing-${r.concept_id}`}>
                          ⚠ No competency statement authored yet. Edit
                          the contract to add one — this defines what
                          the assessment measures.
                        </p>
                      )}
                    </div>
                    {r.competency_statement && (
                      <div className="mt-3 pt-3 border-t border-[var(--iddl-rule)]">
                        <div className="label-mono mb-1 text-[var(--iddl-muted)]">Legacy competency statement</div>
                        <div className="text-xs leading-relaxed">{r.competency_statement}</div>
                      </div>
                    )}
                  </div>

                  <div className="grid lg:grid-cols-2 gap-4">
                  <Field label="Available actions">
                    {(r.available_actions || []).join(", ") || "—"}
                  </Field>
                  <Field label="Required events">
                    {(r.required_events || []).join(", ") || "—"}
                  </Field>
                  <Field label="Critical merit events">
                    {(r.critical_merit_events || []).join(", ") || "—"}
                  </Field>
                  <Field label="Auto-fail events">
                    {(r.auto_fail_events || []).join(", ") || "—"}
                  </Field>
                  <Field label="Valid final decisions">
                    {(r.valid_final_decisions || []).join(", ") || "—"}
                  </Field>
                  <Field label="Sequence rules">
                    {(r.sequence_rules && r.sequence_rules.length)
                      ? r.sequence_rules
                          .map((s) =>
                            typeof s === "object"
                              ? `${s.before || "?"} → ${s.after || "?"}`
                              : String(s),
                          )
                          .join("; ")
                      : "—"}
                  </Field>
                  <Field label="Reflection prompt" wide>
                    {r.reflection_prompt || "—"}
                  </Field>
                  <Field label="Required reflection themes">
                    {(r.reflection_required_themes || []).join(", ") || "—"}
                  </Field>
                  <Field label="Remediation message" wide>
                    {r.remediation_message || "—"}
                  </Field>
                  <Field label="Scenario text" wide>
                    <pre className="text-xs whitespace-pre-wrap max-h-48 overflow-y-auto bg-white p-2 border border-[var(--iddl-rule)]">
                      {stringify(r.scenario_text) || "—"}
                    </pre>
                  </Field>
                  <Field label="Scoring rules" wide>
                    <pre className="text-xs whitespace-pre-wrap">
                      {JSON.stringify(r.scoring_rules || {}, null, 2)}
                    </pre>
                  </Field>
                  <div className="lg:col-span-2 flex justify-end">
                    <Button
                      onClick={() => startEdit(r)}
                      className="rounded-none btn-ink"
                      size="sm"
                      data-testid={`btn-config-edit-${r.concept_id}`}
                    >
                      <Pencil size={12} className="mr-2" /> Edit contract
                    </Button>
                  </div>
                  </div>

                  {/* Iter-26 (P1) — language pack authoring. Opens an
                      inline tabbed editor for the EN/AR/MI slots so
                      admins can author or MT-seed translations
                      without leaving the row. */}
                  {showLangPackFor === r.concept_id ? (
                    <div className="mt-4">
                      <LanguagePackEditor
                        conceptId={r.concept_id}
                        onClose={() => setShowLangPackFor(null)}
                      />
                    </div>
                  ) : (
                    <div className="mt-3 flex justify-end">
                      <Button
                        onClick={() => setShowLangPackFor(r.concept_id)}
                        variant="outline"
                        className="rounded-none"
                        size="sm"
                        data-testid={`btn-langpack-${r.concept_id}`}
                      >
                        🌐 Translate (EN / AR / MI)
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {isOpen && isEditing && draft && (
                <div className="px-4 pb-4 pt-2 bg-[var(--iddl-paper)] space-y-3" data-testid={`config-edit-${r.concept_id}`}>
                  <div className="grid lg:grid-cols-2 gap-3">
                    <EditField label={`Simulation type · ${r.subdomain && r.environment ? "auto-resolved from (subdomain, environment)" : "manual (non-AI concept)"}`}>
                      {(() => {
                        // Phase 6 · When the concept sits in the AI
                        // Competency subdomain map, the primitive is
                        // deterministically derived from
                        // (subdomain, environment) — admin never
                        // picks it manually. For DS / CS concepts
                        // (env=null) the primitive is still hand-
                        // authored via the dropdown.
                        const AI_SUBDOMAINS = ["AI Interaction", "AI Evaluation", "Responsible AI Use"];
                        const isResolved = AI_SUBDOMAINS.includes(r.subdomain) && !!r.environment;
                        if (isResolved) {
                          return (
                            <div
                              className={`w-full border border-[var(--iddl-rule)] px-2 py-2 text-sm bg-zinc-50 label-mono ${SIM_BADGE_COLOR[draft.simulation_type] || ""}`}
                              data-testid={`edit-sim-resolved-${r.concept_id}`}
                              title={`Resolved from subdomain=${r.subdomain} + environment=${r.environment}`}
                            >
                              {draft.simulation_type}
                              <span className="ms-2 label-mono text-[10px] text-zinc-500">
                                (auto-resolved · read-only)
                              </span>
                            </div>
                          );
                        }
                        return (
                          <select
                            value={draft.simulation_type}
                            onChange={(e) => setDraft({ ...draft, simulation_type: e.target.value })}
                            className="w-full border border-[var(--iddl-rule)] bg-white px-2 py-2 text-sm rounded-none"
                            data-testid={`edit-sim-${r.concept_id}`}
                          >
                            <option value="">(none — legacy only)</option>
                            {simTypes.map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                        );
                      })()}
                    </EditField>
                    <EditField label="Task · candidate-facing instruction" wide>
                      <textarea
                        value={draft.candidate_task}
                        onChange={(e) => setDraft({ ...draft, candidate_task: e.target.value })}
                        className="w-full border border-[var(--iddl-rule)] bg-white px-2 py-2 text-sm rounded-none min-h-[64px]"
                        placeholder="Action-oriented instruction the candidate reads when the simulation opens (e.g. 'Review the AI response below. Decide whether the claim should be accepted, rejected, or verified before use. Use available tools and justify your decision.')"
                        data-testid={`edit-task-${r.concept_id}`}
                      />
                    </EditField>
                    <EditField label="Goal · competency statement" wide>
                      <textarea
                        value={draft.candidate_goal}
                        onChange={(e) => setDraft({ ...draft, candidate_goal: e.target.value })}
                        className="w-full border border-[var(--iddl-rule)] bg-white px-2 py-2 text-sm rounded-none min-h-[64px]"
                        placeholder="What this assessment measures — abstract competency outcome."
                        data-testid={`edit-goal-${r.concept_id}`}
                      />
                    </EditField>
                    <EditField label="Available actions (csv)">
                      <Input
                        value={draft.available_actions_csv}
                        onChange={(e) => setDraft({ ...draft, available_actions_csv: e.target.value })}
                        className="rounded-none"
                        data-testid={`edit-actions-${r.concept_id}`}
                      />
                    </EditField>
                    <EditField label="Required events (csv)">
                      <Input
                        value={draft.required_events_csv}
                        onChange={(e) => setDraft({ ...draft, required_events_csv: e.target.value })}
                        className="rounded-none"
                        data-testid={`edit-required-${r.concept_id}`}
                      />
                    </EditField>
                    <EditField label="Critical merit events (csv)">
                      <Input
                        value={draft.critical_merit_events_csv}
                        onChange={(e) => setDraft({ ...draft, critical_merit_events_csv: e.target.value })}
                        className="rounded-none"
                        data-testid={`edit-critical-${r.concept_id}`}
                      />
                    </EditField>
                    <EditField label="Auto-fail events (csv)">
                      <Input
                        value={draft.auto_fail_events_csv}
                        onChange={(e) => setDraft({ ...draft, auto_fail_events_csv: e.target.value })}
                        className="rounded-none"
                        data-testid={`edit-autofail-${r.concept_id}`}
                      />
                    </EditField>
                    <EditField label="Valid final decisions (csv)">
                      <Input
                        value={draft.valid_final_decisions_csv}
                        onChange={(e) => setDraft({ ...draft, valid_final_decisions_csv: e.target.value })}
                        className="rounded-none"
                        data-testid={`edit-finals-${r.concept_id}`}
                      />
                    </EditField>
                    <EditField label="Sequence rules (JSON array of {before, after})">
                      <textarea
                        value={draft.sequence_rules_raw}
                        onChange={(e) => setDraft({ ...draft, sequence_rules_raw: e.target.value })}
                        className="w-full border border-[var(--iddl-rule)] bg-white px-2 py-2 text-xs font-mono rounded-none min-h-[64px]"
                        data-testid={`edit-sequence-${r.concept_id}`}
                      />
                    </EditField>
                    <EditField label="Reflection prompt" wide>
                      <textarea
                        value={draft.reflection_prompt}
                        onChange={(e) => setDraft({ ...draft, reflection_prompt: e.target.value })}
                        className="w-full border border-[var(--iddl-rule)] bg-white px-2 py-2 text-sm rounded-none min-h-[80px]"
                        data-testid={`edit-reflection-prompt-${r.concept_id}`}
                      />
                    </EditField>
                    <EditField label="Reflection required themes">
                      {draft.reflection_themes_mode === "json" ? (
                        <>
                          <div className="label-mono text-xs text-amber-700 mb-1">
                            Themes for this concept are stored as JSON objects
                            (preserved as-authored).
                          </div>
                          <textarea
                            value={draft.reflection_themes_raw}
                            onChange={(e) => setDraft({ ...draft, reflection_themes_raw: e.target.value })}
                            className="w-full border border-[var(--iddl-rule)] bg-white px-2 py-2 text-xs font-mono rounded-none min-h-[96px]"
                            data-testid={`edit-themes-${r.concept_id}`}
                          />
                        </>
                      ) : (
                        <Input
                          value={draft.reflection_themes_csv}
                          onChange={(e) => setDraft({ ...draft, reflection_themes_csv: e.target.value })}
                          className="rounded-none"
                          placeholder="comma-separated themes"
                          data-testid={`edit-themes-${r.concept_id}`}
                        />
                      )}
                    </EditField>
                    <EditField label="Remediation message" wide>
                      <textarea
                        value={draft.remediation_message}
                        onChange={(e) => setDraft({ ...draft, remediation_message: e.target.value })}
                        className="w-full border border-[var(--iddl-rule)] bg-white px-2 py-2 text-sm rounded-none min-h-[64px]"
                        data-testid={`edit-remediation-${r.concept_id}`}
                      />
                    </EditField>
                    <EditField label="Scenario text (string or JSON)" wide>
                      <textarea
                        value={draft.scenario_text_raw}
                        onChange={(e) => setDraft({ ...draft, scenario_text_raw: e.target.value })}
                        className="w-full border border-[var(--iddl-rule)] bg-white px-2 py-2 text-xs font-mono rounded-none min-h-[160px]"
                        data-testid={`edit-scenario-${r.concept_id}`}
                      />
                    </EditField>
                  </div>
                  <div className="flex justify-end gap-2 pt-2 border-t border-[var(--iddl-rule)]">
                    <Button
                      onClick={cancelEdit}
                      variant="outline"
                      className="rounded-none"
                      size="sm"
                      disabled={saving}
                      data-testid={`btn-config-cancel-${r.concept_id}`}
                    >
                      <X size={12} className="mr-2" /> Cancel
                    </Button>
                    <Button
                      onClick={() => saveEdit(r.concept_id, r.environment)}
                      className="rounded-none btn-ink"
                      size="sm"
                      disabled={saving}
                      data-testid={`btn-config-save-${r.concept_id}`}
                    >
                      {saving ? (
                        <Loader2 size={12} className="mr-2 animate-spin" />
                      ) : (
                        <Save size={12} className="mr-2" />
                      )}
                      Save contract
                    </Button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-4">
        <Button
          variant="outline"
          className="rounded-none"
          onClick={load}
          data-testid="btn-config-refresh"
        >
          {t("admin.refresh")}
        </Button>
      </div>
    </div>
  );
}

function Field({ label, children, wide }) {
  return (
    <div className={wide ? "lg:col-span-2" : ""}>
      <div className="label-mono mb-1">{label}</div>
      <div className="text-sm break-words">{children}</div>
    </div>
  );
}

function EditField({ label, wide, children }) {
  return (
    <div className={wide ? "lg:col-span-2" : ""}>
      <div className="label-mono mb-1">{label}</div>
      {children}
    </div>
  );
}


function BulkImportPreviewModal({ preview, file, onCommit, onCancel, committing }) {
  const changes = (preview.applied || []).filter((a) => !a.no_change);
  const failed = preview.failed || [];
  const noChange = preview.total_no_change || 0;
  return (
    <div
      className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 p-6 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      data-testid="bulk-import-preview-modal"
    >
      <div className="bg-white border border-[var(--iddl-rule)] max-w-4xl w-full">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-[var(--iddl-rule)]">
          <FileSpreadsheet size={16} />
          <div>
            <div className="font-display font-semibold">Bulk import preview</div>
            <div className="label-mono text-[var(--iddl-muted)]">{file?.name}</div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={committing}
            className="ms-auto p-1 hover:bg-[var(--iddl-paper)]"
            aria-label="Close"
            data-testid="btn-bulk-import-close"
          >
            <X size={16} />
          </button>
        </div>

        <div className="px-4 py-3 grid grid-cols-3 gap-3 border-b border-[var(--iddl-rule)] bg-[var(--iddl-paper)]">
          <Stat label="Will change" value={changes.length} testid="bulk-stat-changes" emphasis />
          <Stat label="No change" value={noChange} testid="bulk-stat-nochange" />
          <Stat label="Failed" value={failed.length} testid="bulk-stat-failed" warn={failed.length > 0} />
        </div>

        <div className="max-h-[60vh] overflow-y-auto" data-testid="bulk-import-diff-list">
          {changes.length === 0 && failed.length === 0 && (
            <div className="px-4 py-6 text-sm text-[var(--iddl-muted)]">
              The workbook matches the current database exactly. There is nothing to commit.
            </div>
          )}
          {changes.map((c) => (
            <div key={c.concept_id} className="px-4 py-3 border-b border-[var(--iddl-rule)]" data-testid={`bulk-diff-${c.concept_id}`}>
              <div className="flex items-center gap-2 mb-2">
                <code className="label-mono">{c.concept_id}</code>
                <span className="label-mono px-2 py-0.5 border border-emerald-300 bg-emerald-50 text-emerald-900">
                  {Object.keys(c.diff || {}).length} field{Object.keys(c.diff || {}).length === 1 ? "" : "s"} changed
                </span>
                <span className="label-mono ms-auto text-[var(--iddl-muted)]">
                  → version will bump + hash recomputed
                </span>
              </div>
              <table className="text-xs w-full">
                <thead>
                  <tr className="text-left text-[var(--iddl-muted)]">
                    <th className="label-mono w-40 align-top">field</th>
                    <th className="label-mono align-top">from</th>
                    <th className="label-mono align-top">to</th>
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(c.diff || {}).map(([field, dv]) => (
                    <tr key={field} className="align-top">
                      <td className="font-mono pr-2 py-1">{field}</td>
                      <td className="pr-2 py-1 text-rose-700">
                        <pre className="whitespace-pre-wrap break-words max-w-md">{JSON.stringify(dv.from)?.slice(0, 200) || "—"}</pre>
                      </td>
                      <td className="py-1 text-emerald-700">
                        <pre className="whitespace-pre-wrap break-words max-w-md">{JSON.stringify(dv.to)?.slice(0, 200) || "—"}</pre>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
          {failed.length > 0 && (
            <div className="px-4 py-3 border-t-2 border-rose-300 bg-rose-50">
              <div className="flex items-center gap-2 mb-2">
                <AlertTriangle size={14} className="text-rose-700" />
                <strong className="text-rose-800">{failed.length} row{failed.length === 1 ? "" : "s"} could not be applied</strong>
              </div>
              <ul className="list-disc pl-6 text-xs">
                {failed.map((f, i) => (
                  <li key={`${f.concept_id}-${i}`}>
                    <code>{f.concept_id || "(no id)"}</code> — {f.reason}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 px-4 py-3 border-t border-[var(--iddl-rule)]">
          <Button
            variant="outline"
            className="rounded-none"
            onClick={onCancel}
            disabled={committing}
            data-testid="btn-bulk-import-cancel"
          >
            <X size={12} className="mr-2" /> Cancel
          </Button>
          <Button
            className="rounded-none btn-ink"
            onClick={onCommit}
            disabled={committing || changes.length === 0}
            data-testid="btn-bulk-import-commit"
          >
            {committing ? (
              <Loader2 size={12} className="mr-2 animate-spin" />
            ) : (
              <Save size={12} className="mr-2" />
            )}
            Commit {changes.length} change{changes.length === 1 ? "" : "s"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, testid, emphasis, warn }) {
  return (
    <div data-testid={testid}>
      <div className="label-mono">{label}</div>
      <div
        className={`font-display text-2xl ${warn ? "text-rose-700" : emphasis ? "text-emerald-700" : ""}`}
      >
        {value}
      </div>
    </div>
  );
}
